import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { createColumnHelper } from "@tanstack/react-table";
import { useGroups } from "../../../../../../Settings/General/OrganisationLevelGroups/context";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../../../../../utils/hooks/company";
import { renderUserName } from "../../../../../../../utils/helpers/user";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";
import { useOrgSettingsResourceLabel } from "../../../../../../../utils/hooks/useOrgSettingsResourceLabel";

const columnHelper = createColumnHelper();

export const useColumns = (viewBy: string) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const { allocatedGroups } = useGroups();
    const renderOrgLevelResourceLabel = useOrgSettingsResourceLabel();

    const sortedColumns = useMemo(() => {
        const allColumns: any = [
            columnHelper.accessor("user", {
                id: "user",
                header: t("user"),
                cell: ({ row }: any) => {
                    const user = row.original.resource;
                    if (typeof user === "object") return renderUserName(user);
                },
            }),
            columnHelper.accessor("date", {
                id: "date",
                header: t("date"),
                cell: ({ row }: any) => {
                    const date = row.original?.date;
                    return date ? moment(date).format(dateFormat) : "-";
                },
            }),
            columnHelper.accessor("startDate", {
                id: "startDate",
                header: t("start"),
                cell: ({ row }: any) => {
                    const start = row.original?.startDate;
                    return start ? moment(start).format(timeFormat) : "-";
                },
            }),
            columnHelper.accessor("endDate", {
                id: "endDate",
                header: t("end"),
                cell: ({ row }: any) => {
                    const end = row.original?.endDate;
                    return end ? moment(end).format(timeFormat) : "-";
                },
            }),
            columnHelper.accessor("payCode", {
                id: "payCode",
                header: t("pay-code"),
                cell: ({ row }: any) => {
                    const payCode = row.original?.payCode || row.original?.sections?.[0]?.payCode;
                    return payCode ? renderSettingsResourceLabel(payCode) : "-"
                }
            }),
            columnHelper.accessor("job", {
                id: "job",
                header: t("job"),
                cell: ({ row }: any) => {
                    const job = row.original?.job || row.original.sections?.[0]?.job;
                    return job ? renderSettingsResourceLabel(job) : " -";
                },
            }),

            columnHelper.accessor("location", {
                id: "location",
                header: t("location"),
                cell: ({ row }: any) => {
                    const location = row.original?.location || row.original.sections?.[0]?.location;
                    return location ? renderSettingsResourceLabel(location) : " -";
                },
            }),
        ];

        if (allocatedGroups?.length > 0) {
            allocatedGroups.map((group: any) =>
                allColumns.push(columnHelper.accessor(group.level.replaceAll("_", ""), {
                    id: group.level.replaceAll("_", ""),
                    header: group.description,
                    cell: ({ row }: any) => {
                        const level = group.level.replaceAll("_", "");
                        const value = row.original?.[level] || row.original?.sections?.[0]?.[level];
                        return value ? renderOrgLevelResourceLabel(value) : "-";
                    }
                }))
            )
        }

        return [
            ...allColumns.filter((col: any) => col.id === viewBy),
            ...allColumns.filter((col: any) => col.id !== viewBy),
        ];
    }, [viewBy, dateFormat, timeFormat, allocatedGroups, t, renderOrgLevelResourceLabel]);

    return sortedColumns;
};
